// TYPES

import { FormEvent, ReactNode, useEffect, useState } from "react";
import Image from "./Image";
import "../stylesheets/event.scss";
import { isEditor } from "../services/utils";
import EditButton from "./EditButton";
import { deleteEvent, editEvent } from "../services/api";
import { useNotification } from "../contexts/NotificationContext";

type EventProps = {
	id: string;
	title: string;
	date: string;
	image: string;
	children?: ReactNode;
};

// COMPONENT

function Event({ title, date, children, id, image }: EventProps) {
	const { notify } = useNotification();
	// STATES
	const [imageId, setImageId] = useState<string>(image);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	// USE EFFECTS
	const formatDate = (date: Date): string => {
		return date.toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};
	const formatForInput = (dateString: string): string => {
		const [day, month, year] = dateString.split("/");
		return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
	};
	const getTextFromChildren = (children: ReactNode): string => {
		if (typeof children === "string") return children;
		if (Array.isArray(children)) {
			return children.map((child) => getTextFromChildren(child)).join("");
		}
		if (typeof children === "object" && "props" in (children as any)) {
			return getTextFromChildren((children as any).props.children);
		}
		return "";
	};

	// HANDLES
	const handleDelete = async (id: string) => {
		const response = await deleteEvent(id);
		if (response) {
			notify({ type: "validation", title: "Évènement supprimé" });
		} else {
			notify({
				type: "error",
				title: "Impossible de supprimer l'évènement",
			});
		}
	};
	const handleEditing = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		const title = formData.get("title") as string;
		const date = formData.get("date") as string;
		const description = formData.get("description") as string;
		const updatedEvent = {
			id,
			title,
			date,
			description,
			imageName: imageId,
		};
		const response = await editEvent(updatedEvent);
		if (response) {
			notify({ type: "validation", title: "Évènement modifié" });
		} else {
			notify({
				type: "error",
				title: "Impossible de modifier l'évènement...",
			});
		}
	};
	// TSX
	return (
		<div className="event">
			<Image id={imageId} directory={"gallery"} />

			{isEditing ? (
				<form onSubmit={(e) => handleEditing(e)} id="event-edit-form">
					<div className="title-date">
						<input
							type="text"
							name="title"
							defaultValue={title}
							required
						/>
						<input
							type="date"
							required
							name="date"
							defaultValue={formatForInput(
								formatDate(new Date(date)),
							)}
						/>
					</div>
					<textarea
						name="description"
						defaultValue={
							children ? getTextFromChildren(children) : ""
						}
					/>
				</form>
			) : (
				<>
					<div className="title-date">
						<h3>{title}</h3>
						<span>{formatDate(new Date(date))}</span>
					</div>
					{children}
				</>
			)}
			{isEditor() && (
				<div className="edit-buttons">
					{isEditing && (
						<EditButton
							type="submit"
							variant="send"
							form="event-edit-form"
						/>
					)}
					<EditButton
						variant="edit"
						type="button"
						onClick={() => setIsEditing(!isEditing)}
					/>
					<EditButton
						variant="delete"
						type="button"
						onClick={() => handleDelete(id)}
					/>
				</div>
			)}
		</div>
	);
}

export default Event;
